import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../utils/config';
import './ResendTestPage.css'; // Reuse same styling

interface EmailRecord {
    _id: string;
    direction?: 'sent' | 'received';
    from_email: string;
    to_email: string;
    subject: string;
    content?: string;
    full_content?: string;
    sent_at?: string;
    created_at?: string;
    zepto_message_id?: string;
    imap_message_id?: string;
    in_reply_to?: string;
    references?: string;
    is_reply?: boolean;
}

interface ConnectionStatus {
    zepto: {
        status: string;
        api_key_set?: boolean;
        from_email?: string;
        message?: string;
    };
    imap: {
        status: string;
        host?: string;
        port?: number;
        username?: string;
        message?: string;
    };
}

const ZeptoTestPage: React.FC = () => {
    const { token } = useAuth();
    const [emails, setEmails] = useState<EmailRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [checking, setChecking] = useState(false);
    const [testingConnection, setTestingConnection] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null);
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);

    // Form state
    const [formData, setFormData] = useState({
        to_email: '',
        subject: 'Zepto Mail Test - Reply to this email!',
        message: 'This is a test email sent via Zepto Mail. Reply to test email threading!'
    });

    // Fetch emails from MongoDB
    const fetchEmails = async () => {
        const startTime = Date.now();
        console.log('🔄 [FETCH EMAILS] Starting...');
        console.log('🔄 [FETCH EMAILS] API endpoint:', `${API_ENDPOINTS.BASE_URL}/zepto-test/emails`);
        console.log('🔄 [FETCH EMAILS] Token present:', !!token);

        setLoading(true);
        try {
            const headers: HeadersInit = {
                'Content-Type': 'application/json'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            console.log('🔄 [FETCH EMAILS] Request headers:', { ...headers, Authorization: token ? 'Bearer ***' : 'none' });

            const response = await fetch(`${API_ENDPOINTS.BASE_URL}/zepto-test/emails`, {
                headers
            });

            const elapsedTime = Date.now() - startTime;
            console.log('🔄 [FETCH EMAILS] Response received:', {
                status: response.status,
                statusText: response.statusText,
                elapsed_ms: elapsedTime
            });

            if (response.ok) {
                const data = await response.json();
                console.log('✅ [FETCH EMAILS] Success:', {
                    email_count: data.emails?.length || 0,
                    total_count: data.count,
                    elapsed_ms: elapsedTime
                });
                setEmails(data.emails || []);
            } else {
                const errorText = await response.text();
                console.error('❌ [FETCH EMAILS] Failed:', {
                    status: response.status,
                    statusText: response.statusText,
                    error_text: errorText,
                    elapsed_ms: elapsedTime
                });
                setMessage({ type: 'error', text: `Failed to fetch emails: ${response.status} ${errorText}` });
            }
        } catch (error: any) {
            const elapsedTime = Date.now() - startTime;
            console.error('❌ [FETCH EMAILS] Exception:', {
                error: error.message,
                stack: error.stack,
                elapsed_ms: elapsedTime
            });
            setMessage({ type: 'error', text: error.message || 'Error fetching emails' });
        } finally {
            setLoading(false);
            console.log('🔄 [FETCH EMAILS] Completed');
        }
    };

    // Send test email via Zepto
    const sendEmail = async () => {
        if (!formData.to_email) {
            setMessage({ type: 'error', text: 'Please fill in recipient email' });
            return;
        }

        const startTime = Date.now();
        console.log('📧 [SEND EMAIL] Button clicked');
        console.log('📧 [SEND EMAIL] Form data:', {
            to_email: formData.to_email,
            subject: formData.subject,
            message_length: formData.message.length
        });
        console.log('📧 [SEND EMAIL] API endpoint:', `${API_ENDPOINTS.BASE_URL}/zepto-test/send-test-email`);
        console.log('📧 [SEND EMAIL] Token present:', !!token);

        setSending(true);
        setMessage(null);

        try {
            const headers: HeadersInit = {
                'Content-Type': 'application/json'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            console.log('📧 [SEND EMAIL] Request headers:', { ...headers, Authorization: token ? 'Bearer ***' : 'none' });
            console.log('📧 [SEND EMAIL] Request body:', formData);

            const response = await fetch(`${API_ENDPOINTS.BASE_URL}/zepto-test/send-test-email`, {
                method: 'POST',
                headers,
                body: JSON.stringify(formData)
            });

            const elapsedTime = Date.now() - startTime;
            console.log('📧 [SEND EMAIL] Response received:', {
                status: response.status,
                statusText: response.statusText,
                elapsed_ms: elapsedTime,
                headers: Object.fromEntries(response.headers.entries())
            });

            const data = await response.json();
            console.log('📧 [SEND EMAIL] Response data:', data);

            if (response.ok) {
                console.log('✅ [SEND EMAIL] Success:', {
                    message_id: data.message_id,
                    zepto_request_id: data.zepto_request_id,
                    email_id: data.email_id,
                    elapsed_ms: elapsedTime
                });
                setMessage({
                    type: 'success',
                    text: `Email sent! Message-ID: ${data.message_id}`
                });
                // Refresh emails after sending
                setTimeout(() => fetchEmails(), 1000);
            } else {
                console.error('❌ [SEND EMAIL] Failed:', {
                    status: response.status,
                    statusText: response.statusText,
                    error_data: data,
                    elapsed_ms: elapsedTime
                });
                setMessage({ type: 'error', text: data.message || `Failed to send email: ${response.status}` });
            }
        } catch (error: any) {
            const elapsedTime = Date.now() - startTime;
            console.error('❌ [SEND EMAIL] Exception:', {
                error: error.message,
                stack: error.stack,
                elapsed_ms: elapsedTime
            });
            setMessage({ type: 'error', text: error.message || 'Error sending email' });
        } finally {
            setSending(false);
            console.log('📧 [SEND EMAIL] Completed');
        }
    };

    // Check IMAP for new emails
    const checkIMAP = async () => {
        const startTime = Date.now();
        console.log('📥 [CHECK IMAP] Button clicked');
        console.log('📥 [CHECK IMAP] API endpoint:', `${API_ENDPOINTS.BASE_URL}/zepto-test/check-imap`);
        console.log('📥 [CHECK IMAP] Token present:', !!token);

        setChecking(true);
        setMessage({ type: 'info', text: 'Checking IMAP for new emails...' });

        try {
            const headers: HeadersInit = {
                'Content-Type': 'application/json'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            console.log('📥 [CHECK IMAP] Request headers:', { ...headers, Authorization: token ? 'Bearer ***' : 'none' });
            console.log('📥 [CHECK IMAP] Sending request...');

            const response = await fetch(`${API_ENDPOINTS.BASE_URL}/zepto-test/check-imap`, {
                headers
            });

            const elapsedTime = Date.now() - startTime;
            console.log('📥 [CHECK IMAP] Response received:', {
                status: response.status,
                statusText: response.statusText,
                elapsed_ms: elapsedTime,
                headers: Object.fromEntries(response.headers.entries())
            });

            const data = await response.json();
            console.log('📥 [CHECK IMAP] Response data:', data);

            if (response.ok) {
                console.log('✅ [CHECK IMAP] Success:', {
                    new_emails_count: data.new_emails?.length || 0,
                    total_in_inbox: data.total_in_inbox,
                    new_emails: data.new_emails,
                    elapsed_ms: elapsedTime
                });
                setMessage({
                    type: 'success',
                    text: `Found ${data.new_emails?.length || 0} new email(s). Total in inbox: ${data.total_in_inbox}`
                });
                // Refresh email list
                fetchEmails();
            } else {
                console.error('❌ [CHECK IMAP] Failed:', {
                    status: response.status,
                    statusText: response.statusText,
                    error_data: data,
                    details: data.details,
                    hint: data.details?.hint,
                    elapsed_ms: elapsedTime
                });
                const errorMessage = data.details?.hint 
                    ? `${data.message || `Failed to check IMAP: ${response.status}`}\n\nHint: ${data.details.hint}`
                    : data.message || `Failed to check IMAP: ${response.status}`;
                setMessage({ type: 'error', text: errorMessage });
            }
        } catch (error: any) {
            const elapsedTime = Date.now() - startTime;
            console.error('❌ [CHECK IMAP] Exception:', {
                error: error.message,
                stack: error.stack,
                elapsed_ms: elapsedTime
            });
            setMessage({ type: 'error', text: error.message || 'Error checking IMAP' });
        } finally {
            setChecking(false);
            console.log('📥 [CHECK IMAP] Completed');
        }
    };

    // Test connections
    const testConnections = async () => {
        const startTime = Date.now();
        console.log('🔌 [TEST CONNECTION] Button clicked');
        console.log('🔌 [TEST CONNECTION] API endpoint:', `${API_ENDPOINTS.BASE_URL}/zepto-test/test-connection`);
        console.log('🔌 [TEST CONNECTION] Token present:', !!token);

        setTestingConnection(true);
        try {
            const headers: HeadersInit = {
                'Content-Type': 'application/json'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            console.log('🔌 [TEST CONNECTION] Request headers:', { ...headers, Authorization: token ? 'Bearer ***' : 'none' });
            console.log('🔌 [TEST CONNECTION] Sending request...');

            const response = await fetch(`${API_ENDPOINTS.BASE_URL}/zepto-test/test-connection`, {
                headers
            });

            const elapsedTime = Date.now() - startTime;
            console.log('🔌 [TEST CONNECTION] Response received:', {
                status: response.status,
                statusText: response.statusText,
                elapsed_ms: elapsedTime,
                headers: Object.fromEntries(response.headers.entries())
            });

            const data = await response.json();
            console.log('🔌 [TEST CONNECTION] Response data:', data);

            if (response.ok) {
                console.log('✅ [TEST CONNECTION] Success:', {
                    zepto_status: data.connections?.zepto?.status,
                    imap_status: data.connections?.imap?.status,
                    zepto_details: data.connections?.zepto,
                    imap_details: data.connections?.imap,
                    elapsed_ms: elapsedTime
                });
                
                // Log detailed IMAP error if present
                if (data.connections?.imap?.status === 'error') {
                    console.error('❌ [TEST CONNECTION] IMAP Error Details:', {
                        message: data.connections.imap.message,
                        hint: data.connections.imap.hint,
                        host: data.connections.imap.host,
                        port: data.connections.imap.port,
                        username: data.connections.imap.username,
                        ssl: data.connections.imap.ssl,
                        full_error: data.connections.imap
                    });
                    
                    // Show hint in UI if available
                    if (data.connections.imap.hint) {
                        console.warn('💡 [TEST CONNECTION] IMAP Hint:', data.connections.imap.hint);
                    }
                }
                
                setConnectionStatus(data.connections);
                setMessage({ type: 'success', text: 'Connection test completed' });
            } else {
                console.error('❌ [TEST CONNECTION] Failed:', {
                    status: response.status,
                    statusText: response.statusText,
                    error_data: data,
                    elapsed_ms: elapsedTime
                });
                setMessage({ type: 'error', text: data.message || `Failed to test connections: ${response.status}` });
            }
        } catch (error: any) {
            const elapsedTime = Date.now() - startTime;
            console.error('❌ [TEST CONNECTION] Exception:', {
                error: error.message,
                stack: error.stack,
                elapsed_ms: elapsedTime
            });
            setMessage({ type: 'error', text: error.message || 'Error testing connections' });
        } finally {
            setTestingConnection(false);
            console.log('🔌 [TEST CONNECTION] Completed');
        }
    };

    // Auto-fetch emails on mount and every 5 seconds
    useEffect(() => {
        fetchEmails();
        const interval = setInterval(fetchEmails, 5000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="resend-test-page">
            <div className="page-header">
                <h1>🚀 Zepto Mail + IMAP Test Page</h1>
                <p style={{ color: '#666', marginTop: '8px' }}>
                    Test Zepto Mail (sending) and IMAP (receiving) integration
                </p>
            </div>

            {/* Connection Status */}
            <div className="test-section">
                <div className="section-header">
                    <h2>🔌 Connection Status</h2>
                    <button
                        onClick={testConnections}
                        disabled={testingConnection}
                        className="btn-secondary"
                    >
                        {testingConnection ? 'Testing...' : 'Test Connections'}
                    </button>
                </div>

                {connectionStatus && (
                    <div className="connection-status-grid">
                        <div className={`status-card ${connectionStatus.zepto.status}`}>
                            <h3>📤 Zepto Mail (SMTP)</h3>
                            <div className="status-badge">{connectionStatus.zepto.status}</div>
                            {connectionStatus.zepto.from_email && (
                                <p>From: {connectionStatus.zepto.from_email}</p>
                            )}
                            {connectionStatus.zepto.message && (
                                <p className="error-text">{connectionStatus.zepto.message}</p>
                            )}
                        </div>

                        <div className={`status-card ${connectionStatus.imap.status}`}>
                            <h3>📥 IMAP (Receiving)</h3>
                            <div className="status-badge">{connectionStatus.imap.status}</div>
                            {connectionStatus.imap.host && (
                                <p>Host: {connectionStatus.imap.host}:{connectionStatus.imap.port}</p>
                            )}
                            {connectionStatus.imap.username && (
                                <p>User: {connectionStatus.imap.username}</p>
                            )}
                            {connectionStatus.imap.message && (
                                <p className="error-text">{connectionStatus.imap.message}</p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Send Email Form */}
            <div className="test-section">
                <h2>📤 Send Test Email (Zepto Mail)</h2>

                <div className="form-group">
                    <label>To Email *</label>
                    <input
                        type="email"
                        value={formData.to_email}
                        onChange={(e) => setFormData({ ...formData, to_email: e.target.value })}
                        placeholder="recipient@example.com"
                    />
                </div>

                <div className="form-group">
                    <label>Subject</label>
                    <input
                        type="text"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    />
                </div>

                <div className="form-group">
                    <label>Message</label>
                    <textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={4}
                    />
                </div>

                <button
                    onClick={sendEmail}
                    disabled={sending || !formData.to_email}
                    className="btn-primary"
                >
                    {sending ? 'Sending...' : '📧 Send Test Email'}
                </button>
            </div>

            {/* Check IMAP */}
            <div className="test-section">
                <div className="section-header">
                    <h2>📥 Check IMAP for New Emails</h2>
                    <button
                        onClick={checkIMAP}
                        disabled={checking}
                        className="btn-secondary"
                    >
                        {checking ? 'Checking...' : '🔄 Check IMAP Now'}
                    </button>
                </div>
                <p style={{ color: '#666', fontSize: '14px' }}>
                    Auto-refreshes every 5 seconds. Click button to check immediately.
                </p>
            </div>

            {/* Message Display */}
            {message && (
                <div className={`message message-${message.type}`}>
                    {message.text}
                </div>
            )}

            {/* Email List */}
            <div className="test-section">
                <div className="section-header">
                    <h2>📨 Test Emails ({emails.length})</h2>
                    <button onClick={fetchEmails} className="btn-secondary" disabled={loading}>
                        {loading ? 'Loading...' : '🔄 Refresh'}
                    </button>
                </div>

                {loading ? (
                    <div className="loading">Loading emails...</div>
                ) : emails.length === 0 ? (
                    <div className="empty-state">
                        <p>No test emails yet. Send a test email to get started!</p>
                    </div>
                ) : (
                    <div className="emails-list">
                        {emails.map((email) => (
                            <div key={email._id} className={`email-card ${email.direction}`}>
                                <div className="email-header">
                                    <div className="direction-badge">
                                        {email.direction === 'sent' ? '📤 SENT' : '📥 RECEIVED'}
                                    </div>
                                    <div className="email-meta">
                                        <strong>{email.subject}</strong>
                                        <div className="email-addresses">
                                            <span>From: {email.from_email}</span>
                                            <span>To: {email.to_email}</span>
                                        </div>
                                        <div className="timestamp">
                                            {new Date(email.sent_at || email.created_at || '').toLocaleString()}
                                        </div>
                                    </div>
                                </div>

                                {email.content && (
                                    <div className="email-content">
                                        {email.content.substring(0, 200)}
                                        {email.content.length > 200 && '...'}
                                    </div>
                                )}

                                {/* Threading Information */}
                                {(email.zepto_message_id || email.imap_message_id || email.in_reply_to || email.references) && (
                                    <div className="threading-info">
                                        <h4>🔗 Threading Information:</h4>
                                        {email.zepto_message_id && (
                                            <div className="thread-detail">
                                                <strong>Zepto Message-ID:</strong>
                                                <code>{email.zepto_message_id}</code>
                                            </div>
                                        )}
                                        {email.imap_message_id && (
                                            <div className="thread-detail">
                                                <strong>IMAP Message-ID:</strong>
                                                <code>{email.imap_message_id}</code>
                                            </div>
                                        )}
                                        {email.in_reply_to && (
                                            <div className="thread-detail">
                                                <strong>In-Reply-To:</strong>
                                                <code>{email.in_reply_to}</code>
                                                {email.is_reply && <span className="reply-badge">✅ REPLY</span>}
                                            </div>
                                        )}
                                        {email.references && (
                                            <div className="thread-detail">
                                                <strong>References:</strong>
                                                <code>{email.references}</code>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Instructions */}
            <div className="test-section instructions">
                <h2>📚 Test Instructions</h2>
                <ol>
                    <li>
                        <strong>Setup:</strong> Make sure you've configured Zepto Mail and IMAP credentials in .env
                        <ul>
                            <li>ZEPTO_API_KEY</li>
                            <li>IMAP_HOST, IMAP_PORT, IMAP_USERNAME, IMAP_PASSWORD</li>
                        </ul>
                    </li>
                    <li>
                        <strong>Webhook URL (ngrok ile test için):</strong>
                        <ol style={{ marginLeft: '20px', marginTop: '8px' }}>
                            <li>Terminal'de ngrok'u başlatın:
                                <br />
                                <code style={{ background: '#f0f0f0', padding: '4px 8px', borderRadius: '4px', display: 'inline-block', marginTop: '4px', marginLeft: '8px' }}>
                                    ngrok http 5001
                                </code>
                            </li>
                            <li>ngrok'un verdiği HTTPS URL'ini kopyalayın (örn: <code>https://abc123.ngrok.io</code>)</li>
                            <li>Zepto Mail webhook'u şu URL'e ayarlayın:
                                <br />
                                <code style={{ background: '#f0f0f0', padding: '4px 8px', borderRadius: '4px', display: 'inline-block', marginTop: '4px', marginLeft: '8px' }}>
                                    https://your-ngrok-url.ngrok.io/api/emails/webhook/received
                                </code>
                            </li>
                        </ol>
                    </li>
                    <li>
                        <strong>Test Connection:</strong> Click "Test Connections" to verify setup
                    </li>
                    <li>
                        <strong>Send Email:</strong> Enter your email address and click "Send Test Email"
                    </li>
                    <li>
                        <strong>Reply:</strong> Check your inbox, reply to the email
                    </li>
                    <li>
                        <strong>Check IMAP:</strong> Click "Check IMAP Now" or wait 5 seconds (for manual IMAP polling)
                        <br />
                        <small style={{ color: '#666' }}>Note: Webhook will automatically process replies if configured</small>
                    </li>
                    <li>
                        <strong>Verify Threading:</strong> Reply should show "In-Reply-To" matching sent email's Message-ID
                    </li>
                </ol>
            </div>
        </div>
    );
};

export default ZeptoTestPage;
